import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import { UserCircle, Plus, Search, Edit2, Trash2 } from 'lucide-react';
import apiService from '../../services/api';
import Button from '../../components/UI/Button';
import Input from '../../components/UI/Input';
import Modal from '../../components/UI/Modal';
import Card from '../../components/UI/Card';
import Table from '../../components/UI/Table';
import LoadingSpinner from '../../components/UI/LoadingSpinner';

interface Employee {
  _id: string;
  name: string;
  designation?: string;
  email?: string;
  contactNo?: string;
  isActive: boolean;
}

const EmployeesPage: React.FC = () => {
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState<Employee | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    designation: '',
    email: '',
    contactNo: '',
  });
  const [submitting, setSubmitting] = useState(false);

  const fetchEmployees = useCallback(async () => {
    try {
      setLoading(true);
      const response = await apiService.getEmployees();
      setEmployees(response.data.employees || []);
    } catch (error: any) {
      console.error('Error fetching employees:', error);
      toast.error(error.response?.data?.error || 'Failed to fetch employees');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchEmployees();
  }, [fetchEmployees]);

  const handleOpenModal = (employee?: Employee) => {
    if (employee) {
      setEditingEmployee(employee);
      setFormData({
        name: employee.name,
        designation: employee.designation || '',
        email: employee.email || '',
        contactNo: employee.contactNo || '',
      });
    } else {
      setEditingEmployee(null);
      setFormData({ name: '', designation: '', email: '', contactNo: '' });
    }
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setEditingEmployee(null);
    setFormData({ name: '', designation: '', email: '', contactNo: '' });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      toast.error('Employee name is required');
      return;
    }

    try {
      setSubmitting(true);
      const payload = {
        name: formData.name.trim(),
        designation: formData.designation.trim() || undefined,
        email: formData.email.trim() || undefined,
        contactNo: formData.contactNo.trim() || undefined,
      };
      if (editingEmployee) {
        await apiService.updateEmployee(editingEmployee._id, payload);
        toast.success('Employee updated successfully');
      } else {
        await apiService.createEmployee(payload);
        toast.success('Employee added successfully');
      }
      handleCloseModal();
      fetchEmployees();
    } catch (error: any) {
      console.error('Error saving employee:', error);
      toast.error(error.response?.data?.error || 'Failed to save employee');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this employee?')) return;

    try {
      await apiService.deleteEmployee(id);
      toast.success('Employee deleted successfully');
      fetchEmployees();
    } catch (error: any) {
      console.error('Error deleting employee:', error);
      toast.error(error.response?.data?.error || 'Failed to delete employee');
    }
  };

  const filteredEmployees = employees.filter(
    (emp) =>
      emp.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (emp.designation || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (emp.email || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (emp.contactNo || '').includes(searchTerm)
  );

  const columns = [
    { header: 'Name', accessor: (row: Employee) => row.name },
    { header: 'Designation', accessor: (row: Employee) => row.designation || '-' },
    { header: 'Email', accessor: (row: Employee) => row.email || '-' },
    { header: 'Contact No.', accessor: (row: Employee) => row.contactNo || '-' },
    {
      header: 'Actions',
      accessor: (row: Employee) => (
        <div className="flex gap-0.5">
          <Button
            variant="ghost"
            size="sm"
            className="text-amber-600 hover:text-amber-700 hover:bg-amber-50"
            icon={<Edit2 className="w-4 h-4" />}
            onClick={() => handleOpenModal(row)}
          />
          <Button
            variant="ghost"
            size="sm"
            className="text-red-600 hover:text-red-700 hover:bg-red-50"
            icon={<Trash2 className="w-4 h-4" />}
            onClick={() => handleDelete(row._id)}
          />
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} className="flex justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Employees</h1>
          <p className="mt-2 text-sm text-gray-600">Manage employee information</p>
        </div>
        <Button icon={<Plus className="w-4 h-4" />} onClick={() => handleOpenModal()}>
          Add Employee
        </Button>
      </motion.div>

      <Card padding="md">
        <Input
          placeholder="Search employees..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          icon={<Search className="w-5 h-5" />}
          fullWidth
        />
      </Card>

      <Card padding="none">
        {loading ? (
          <div className="flex justify-center items-center py-12">
            <LoadingSpinner size="lg" />
          </div>
        ) : filteredEmployees.length > 0 ? (
          <Table data={filteredEmployees} columns={columns} emptyMessage="No employees found" />
        ) : (
          <div className="px-6 py-12 text-center">
            <UserCircle className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No employees yet</h3>
            <p className="mt-1 text-sm text-gray-500">Get started by adding your first employee.</p>
            <div className="mt-6">
              <Button icon={<Plus className="w-4 h-4" />} onClick={() => handleOpenModal()}>
                Add Employee
              </Button>
            </div>
          </div>
        )}
      </Card>

      <Modal
        isOpen={modalOpen}
        onClose={handleCloseModal}
        title={editingEmployee ? 'Edit Employee' : 'Add New Employee'}
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <Input
            label="Employee Name *"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            placeholder="Enter employee name"
            required
            fullWidth
          />
          <Input
            label="Designation"
            value={formData.designation}
            onChange={(e) => setFormData({ ...formData, designation: e.target.value })}
            placeholder="Enter designation (optional)"
            fullWidth
          />
          <Input
            label="Email"
            type="email"
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            placeholder="Enter email (optional)"
            fullWidth
          />
          <Input
            label="Contact No."
            value={formData.contactNo}
            onChange={(e) => setFormData({ ...formData, contactNo: e.target.value })}
            placeholder="Enter contact number (optional)"
            fullWidth
          />
          <div className="flex justify-end space-x-3 pt-4">
            <Button type="button" variant="outline" onClick={handleCloseModal} disabled={submitting}>
              Cancel
            </Button>
            <Button type="submit" loading={submitting}>
              {editingEmployee ? 'Update Employee' : 'Add Employee'}
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default EmployeesPage;
